import {
  BugReport,
  Build,
  ExploreOutlined,
  FileOpenOutlined,
  LightbulbOutlined,
  LocalOfferOutlined,
  LockOutlined,
  MapOutlined,
  NotificationsOutlined,
  PrivacyTipOutlined,
  RemoveModeratorOutlined,
  SchoolOutlined,
  SellOutlined,
  ShieldOutlined,
  Speed,
  StorefrontOutlined,
  TableChartOutlined,
  Troubleshoot,
} from '@mui/icons-material';
import {
  Badge,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useReportId } from 'api';
import useExperiments from 'api/experiments';
import { useFlag } from 'api/feature-flags/provider';
import { getPrivacyEnabledOverride } from 'api/utils';
import LogoOnlyUrl from 'asset/sentinel_logo.png';
import Logo from 'components/Logo';
import PureLink from 'components/PureLink';
import React from 'react';

import { AppRoutes } from 'config/app-routes';
import { linksStartingWith } from 'utils/component';

import ConsentDashboardFeatureFlagGuard from './components/featureFlagGuard';
import NewUIBanner from './components/NewUIBanner';
import * as Styled from './NavDrawer.styled';
import NavItem from './NavItem';

type DrawerProps = React.ComponentProps<typeof Drawer>;

//#region types
type NavDrawerProps = {
  variant?: DrawerProps['variant'] | 'mini';
} & Omit<DrawerProps, 'variant'>;
//#endregion

/**
 * Navigation Drawer Component
 * @param open - whether the drawer is open
 * @param onClose - callback to close the drawer
 * @returns {ReactJSXElement}
 */
export default function NavDrawer(props: NavDrawerProps) {
  const reportId = useReportId();
  const { isExperimentEnabled } = useExperiments();
  const sitemapEnabled = useFlag('page.consentDashboard.sitemap');
  const sitemapVisible = useFlag('page.consentDashboard.sitemap.visible');
  const tagVersionsEnabled = useFlag('page.consentDashboard.tagVersions');
  const tagVersionsVisible = useFlag('page.consentDashboard.tagVersions.visible');

  const { variant: _variant, ...rest } = props;
  const isMini = _variant === 'mini';
  const variant = _variant === 'mini' ? 'permanent' : _variant;

  const imgStyle: React.CSSProperties = isMini
    ? {
        padding: '16px 0',
        boxSizing: 'content-box',
      }
    : {};

  return (
    <Styled.Drawer
      variant={variant}
      {...rest}
      style={{
        width: isMini ? '48px' : undefined,
      }}
    >
      <div>
        <PureLink to={`/?reportId=${new URLSearchParams(window.location.search).get('reportId')}`}>
          {true ? (
            <Logo
              small={isMini}
              height={32}
              style={{
                marginTop: '24px',
                marginRight: 'auto',
                marginLeft: '24px',
                marginBottom: '12px',
              }}
            />
          ) : (
            <Styled.Logo
              src={LogoOnlyUrl}
              height={isMini ? '48px' : undefined}
              width={isMini ? '48px' : undefined}
              style={imgStyle}
            />
          )}
        </PureLink>
        <List id="nav-sidebar-list">
          <NavItem isMini={isMini} to="/" active={linksStartingWith(AppRoutes.INSIGHTS)} icon={<LightbulbOutlined />}>
            Data Insights
          </NavItem>

          {/*
          <Item isMini={isMini} disabled to="/admin" icon={<AdminPanelSettingsOutlined />}>
            Admin
          </Item>
          <Item isMini={isMini} disabled to="/technologies" icon={<Storefront />}>
            Technologies
          </Item>
          */}

          <ConsentDashboardFeatureFlagGuard>
            <NavItem isMini={isMini} to="/consent-insights" icon={<Speed />}>
              Consent Insights
            </NavItem>
          </ConsentDashboardFeatureFlagGuard>
          {isExperimentEnabled('pages.privacy.visible') && (
            <NavItem
              isMini={isMini}
              disabled={!isExperimentEnabled('pages.privacy.enabled')}
              to={AppRoutes.PRIVACY}
              icon={
                getPrivacyEnabledOverride(reportId) === undefined ? (
                  <PrivacyTipOutlined />
                ) : (
                  <Badge overlap="circular" badgeContent={<BugReport fontSize="small" />}>
                    <PrivacyTipOutlined />
                  </Badge>
                )
              }
            >
              Privacy
            </NavItem>
          )}
          {isExperimentEnabled('pages.admin.visible') && (
            <NavItem
              disabled={!isExperimentEnabled('pages.admin.enabled')}
              isMini={isMini}
              to={AppRoutes.ADMIN}
              icon={<LockOutlined />}
            >
              Admin
            </NavItem>
          )}
          {sitemapVisible && (
            <NavItem
              disabled={!sitemapEnabled}
              isMini={isMini}
              to={AppRoutes.CONSENT_DASHBOARD_SITEMAP}
              icon={<MapOutlined />}
            >
              Site Map
            </NavItem>
          )}
          {isExperimentEnabled('pages.internalAdmin.visible') && (
            <NavItem
              disabled={!isExperimentEnabled('pages.internalAdmin.enabled')}
              isMini={isMini}
              to={AppRoutes.INTERNAL_ADMIN}
              icon={<ShieldOutlined />}
            >
              Rule Configuration
            </NavItem>
          )}

          {isExperimentEnabled('pages.technology.visible') && (
            <NavItem
              disabled={!isExperimentEnabled('pages.technology.enabled')}
              isMini={isMini}
              to={AppRoutes.TECHNOLOGY}
              icon={<StorefrontOutlined />}
            >
              Technology
            </NavItem>
          )}
          {isExperimentEnabled('pages.notifications.visible') && (
            <NavItem
              disabled={!isExperimentEnabled('pages.notifications.enabled')}
              isMini={isMini}
              to={AppRoutes.NOTIFICATIONS}
              icon={<NotificationsOutlined />}
            >
              Alert Settings
            </NavItem>
          )}
          {isExperimentEnabled('pages.explore.visible') && (
            <NavItem
              disabled={!isExperimentEnabled('pages.explore.enabled')}
              isMini={isMini}
              to={AppRoutes.explore.index()}
              icon={<ExploreOutlined />}
            >
              Data Exploration
            </NavItem>
          )}
          {isExperimentEnabled('pages.dataExport.visible') && (
            <NavItem
              disabled={!isExperimentEnabled('pages.dataExport.enabled')}
              isMini={isMini}
              to={AppRoutes.dataExport.index()}
              icon={<FileOpenOutlined />}
            >
              Data Export
            </NavItem>
          )}

          <NavItem
            isMini={isMini}
            to=""
            icon={<Troubleshoot />}
            subpages={[
              {
                value: '/request-identification/unknown-requests',
                label: 'Unidentified Requests',
                icon: <TableChartOutlined />,
              },
              {
                value: '/request-identification/custom-requests',
                label: 'Custom Requests',
                icon: <Build />,
              },
              {
                value: '/request-identification/custom-tags',
                label: 'Custom Tags',
                icon: <SellOutlined />,
              },
              {
                value: '/request-identification/ignored-requests',
                label: 'Ignored Requests',
                icon: <RemoveModeratorOutlined />,
              },
            ]}
          >
            Request Identification
          </NavItem>
          {tagVersionsVisible && (
            <NavItem
              disabled={!tagVersionsEnabled}
              isMini={isMini}
              to={AppRoutes.CONSENT_DASHBOARD_TAG_VERSIONS}
              icon={<LocalOfferOutlined />}
            >
              Tag Versions
            </NavItem>
          )}
          {isExperimentEnabled('user.isInternal') && window.localStorage.getItem('showInternalTools') === 'true' && (
            <>
              <Divider />
              <NavItem isMini={isMini} to="/internal/tools" icon={<LockOutlined />}>
                Internal Tools
              </NavItem>
            </>
          )}
        </List>
      </div>
      <div>
        {/*
        <List>
          <Item isMini={isMini} to="/help" icon={<HelpOutline />}>
            Help
          </Item>
          <Item isMini={isMini} to="/account" icon={<PersonOutline />}>
            My Account
          </Item>
        </List>
        */}
        <List id="nav-sidebar-list-bottom">
          <ListItemButton
            onClick={() => window.open('https://docs.sentinelinsights.com/29zLiDAHFMPDhOWaTiAw', '_blank')}
          >
            <ListItemIcon>
              <SchoolOutlined />
            </ListItemIcon>
            <ListItemText>Knowledge Base</ListItemText>
          </ListItemButton>
        </List>
        <Stack>{!isMini && <NewUIBanner />}</Stack>
        {!isMini && (
          <>
            <Divider />
            <Stack padding={2}>
              <Typography variant="caption" textAlign="center" display="inline-block">
                &copy;
                {new Date().getFullYear()}
                &nbsp;
                {'Sentinel Insights'}
              </Typography>
              <Typography variant="caption" textAlign="center" display="inline-block">
                All Rights Reserved
              </Typography>
              {isExperimentEnabled('user.isInternal') && (
                <Typography display="inline-block" textAlign="center" variant="caption">
                  {isExperimentEnabled('flag.isProdWebsite')
                    ? // eslint-disable-next-line sentinelinsights/no-mutations
                      `Prod Release ${(process.env.REACT_APP_DASHBOARD_VERSION ?? '').split('/').pop()}`
                    : isExperimentEnabled('flag.isDevWebsite')
                    ? `Beta Commit ${(process.env.REACT_APP_DASHBOARD_VERSION ?? '').slice(0, 7)}`
                    : isExperimentEnabled('flag.isLocalWebsite')
                    ? `Local Environment`
                    : ''}
                </Typography>
              )}
            </Stack>
          </>
        )}
      </div>
    </Styled.Drawer>
  );
}
